import { useState, useEffect } from 'react'
import api, { getFileUrl } from '../utils/api'
import '../styles/viewStoryModal.css'

interface Story {
  id: string
  creator_email: string
  content: string
  media_type: string
  file_path?: string
  visibility_type: string
  created_at: string
  expires_at: string
}

interface Viewer {
  viewer_email: string
  viewed_at: string
}

interface Reaction {
  emoji: string
  users: string
}

interface ViewStoryModalProps {
  story: Story
  allContactStories: Story[]
  onClose: () => void
  onNavigate: (story: Story) => void
  userEmail: string
}

const EMOJI_REACTIONS = ['👍', '❤️', '😂', '😮', '😢', '🔥', '✨', '👏']

export default function ViewStoryModal({ story, allContactStories, onClose, onNavigate, userEmail }: ViewStoryModalProps) {
  const [viewers, setViewers] = useState<Viewer[]>([])
  const [reactions, setReactions] = useState<Reaction[]>([])
  const [showViewers, setShowViewers] = useState(false)
  const [userReaction, setUserReaction] = useState<string | null>(null)
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0)
  const isCreator = story.creator_email === userEmail

  // Ensure the current story is included in allContactStories
  const contactStories = allContactStories.some(s => s.id === story.id) 
    ? allContactStories 
    : [story, ...allContactStories]

  // Sort stories by creation date
  const sortedStories = [...contactStories].sort((a, b) => 
    new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
  )

  useEffect(() => {
    // Find current story index
    const index = sortedStories.findIndex(s => s.id === story.id)
    setCurrentStoryIndex(index >= 0 ? index : 0)
    loadStoryDetails()
  }, [story.id, sortedStories])

  const loadStoryDetails = async () => {
    try {
      const response = await api.get(`/api/stories/${story.id}`)
      setViewers(response.data.viewers || [])
      setReactions(response.data.reactions || [])
      
      // Check if user has already reacted
      const userReactions = response.data.reactions?.filter((r: Reaction) => 
        r.users?.includes(userEmail)
      ) || []
      if (userReactions.length > 0) {
        setUserReaction(userReactions[0].emoji)
      }
    } catch (error) {
      console.error('Erreur lors du chargement de la story:', error)
    }
  }

  const handlePrevious = () => {
    if (currentStoryIndex > 0) {
      const previousStory = sortedStories[currentStoryIndex - 1]
      onNavigate(previousStory)
      setCurrentStoryIndex(currentStoryIndex - 1)
      setShowViewers(false)
      setUserReaction(null)
    }
  }

  const handleNext = () => {
    if (currentStoryIndex < sortedStories.length - 1) {
      const nextStory = sortedStories[currentStoryIndex + 1]
      onNavigate(nextStory)
      setCurrentStoryIndex(currentStoryIndex + 1)
      setShowViewers(false)
      setUserReaction(null)
    }
  }

  const handleReaction = async (emoji: string) => {
    try {
      setUserReaction(userReaction === emoji ? null : emoji)
      if (userReaction !== emoji) {
        await api.post(`/api/stories/${story.id}/reactions`, { emoji })
      }
      loadStoryDetails()
    } catch (error) {
      console.error('Erreur lors de l\'ajout de réaction:', error)
    }
  }

  const formatTime = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('fr-FR')
  }

  return (
    <div className="view-story-overlay" onClick={onClose}>
      <div className="view-story-modal" onClick={(e) => e.stopPropagation()}>
        <button className="close-btn" onClick={onClose}>✕</button>

        {/* Navigation arrows */}
        {sortedStories.length > 1 && (
          <>
            {currentStoryIndex > 0 && (
              <button 
                className="nav-arrow nav-prev"
                onClick={handlePrevious}
                title="Story précédente"
              >
                ◀
              </button>
            )}
            {currentStoryIndex < sortedStories.length - 1 && (
              <button 
                className="nav-arrow nav-next"
                onClick={handleNext}
                title="Story suivante"
              >
                ▶
              </button>
            )}
          </>
        )}

        {/* Story counter */}
        {sortedStories.length > 1 && (
          <div className="story-counter">
            {currentStoryIndex + 1} / {sortedStories.length}
          </div>
        )}

        <div className="story-creator-info">
          <div className="creator-name">
            {story.creator_email}
          </div>
          <div className="story-time">
            {formatTime(story.created_at)}
          </div>
        </div>

        {/* Content */}
        <div className="story-content">
          {story.media_type === 'image' && story.file_path && (
            <img 
              src={getFileUrl(story.file_path)} 
              alt="Story" 
              className="story-image"
              onError={(e) => {
                (e.target as HTMLImageElement).src = 'https://via.placeholder.com/400?text=Image';
              }}
            />
          )}
          {story.media_type === 'video' && story.file_path && (
            <video 
              src={getFileUrl(story.file_path)} 
              className="story-video" 
              controls 
              autoPlay 
            />
          )}
          {story.content && (
            <div className="story-text">
              {story.content}
            </div>
          )}
        </div>

        {/* Reactions */}
        <div className="story-reactions">
          {EMOJI_REACTIONS.map((emoji) => (
            <button 
              key={emoji}
              className={`reaction-btn ${userReaction === emoji ? 'active' : ''}`}
              onClick={() => handleReaction(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>

        {/* Reactions summary */}
        {reactions.length > 0 && (
          <div className="reactions-summary">
            {reactions.map((reaction) => (
              <span key={reaction.emoji} className="reaction-badge">
                {reaction.emoji} {reaction.users?.split(',').length || 0}
              </span>
            ))}
          </div>
        )}

        {/* Viewers */}
        {isCreator && (
          <div className="viewers-section">
            <button 
              className="viewers-toggle"
              onClick={() => setShowViewers(!showViewers)}
            >
              👁️ Vus par {viewers.length}
            </button>

            {showViewers && (
              <div className="viewers-list">
                {viewers.length === 0 ? (
                  <div className="no-viewers">Personne n'a vu cette story</div>
                ) : (
                  viewers.map((viewer) => (
                    <div key={viewer.viewer_email} className="viewer-item">
                      <span className="viewer-email">
                        {viewer.viewer_email}
                      </span>
                      <span className="viewer-time">
                        {formatTime(viewer.viewed_at)}
                      </span>
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {/* Expiration info */}
        <div className="story-expiration">
          <span>Expire le {formatDate(story.expires_at)}</span>
        </div>
      </div>
    </div>
  )
}
